import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  BodyText,
  Box,
  Button,
  Card,
  DateTimePicker,
  Modal,
  Portal,
  RadioButton,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';
import { formatDate } from '@chargify/formatters';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

import { pauseSubscriptionSelectors } from '../../../../machines/pauseSubscription';
import { subscriptionsSelectors } from '../../../../machines';
import { usePauseSubscriptionService } from '../../../../hooks/usePauseSubscriptionService';
import { GlobalStateContext } from '../../../../contexts';
import {
  AutomaticResumeOption,
  OptionsWrapper,
} from '../../../../components/RadioOptions';
type PauseSubscriptionProps = {
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Catalog id
   */
  catalogId: number;
  /**
   * Subscription id
   */
  subscriptionId: number;
};

const ResumeOptions = {
  MANUAL_RESUME: 'manual_resume',
  AUTOMATIC_RESUME: 'automatic_resume',
};

const defaultDate = dayjs().add(7, 'day');

const Content = ({
  onClose,
  displayMode,
  catalogId,
  subscriptionId,
}: PauseSubscriptionProps) => {
  const { t } = useTranslation(['subscriptions-manager']);
  const modalRef = useRef(null);
  const portalContainer = useRef(null);
  const { subscriptionsService } = useContext(GlobalStateContext);

  const toastService = useToastMessages();

  const { subscription } = useSelector(
    subscriptionsService,
    useCallback(
      subscriptionsSelectors.getSubscriptionById(catalogId, subscriptionId),
      []
    )
  );

  const pauseSubscriptionService = usePauseSubscriptionService(
    catalogId,
    subscriptionId
  );

  const [selectedResumeOption, setSelectedResumeOption] = useState(
    subscription.automaticallyResumeAt
      ? ResumeOptions.AUTOMATIC_RESUME
      : ResumeOptions.MANUAL_RESUME
  );

  const pauseSubscription = () => {
    const payload = {
      subscriptionId,
    } as {
      subscriptionId: number;
      automaticallyResumeAt?: string;
    };
    if (selectedResumeOption === ResumeOptions.AUTOMATIC_RESUME) {
      payload.automaticallyResumeAt = selectedDateTime.toISOString();
    }
    pauseSubscriptionService.send('PAUSE_SUBSCRIPTION', payload);
  };

  const alertMessage = () => {
    return selectedResumeOption === 'manual_resume'
      ? t('pause_subscription.manual_resume_description')
      : t('pause_subscription.automatic_resume_description', {
          date: formatDate(selectedDateTime),
        });
  };

  const {
    pauseSubscriptionLoading,
    pauseSubscriptionError,
    pauseSubscriptionSuccess,
  } = useSelector(
    pauseSubscriptionService,
    pauseSubscriptionSelectors.getPauseSubscriptionStatus
  );

  const error = useSelector(
    pauseSubscriptionService,
    pauseSubscriptionSelectors.getErrorCode
  );

  const [selectedDateTime, setSelectedDateTime] = useState(
    subscription.automaticallyResumeAt
      ? dayjs(subscription.automaticallyResumeAt)
      : defaultDate
  );

  useEffect(() => {
    if (pauseSubscriptionSuccess) {
      onClose();

      const toastMessage =
        selectedResumeOption === ResumeOptions.AUTOMATIC_RESUME
          ? t('pause_subscription.your_plan_is_on_hold_until_date', {
              date: formatDate(selectedDateTime),
            })
          : t('pause_subscription.your_plan_is_on_hold');

      toastService.show({
        level: 'success',
        message: toastMessage,
      });
    }
  }, [pauseSubscriptionSuccess]);

  return (
    <>
      <Card
        border={displayMode === 'layer' ? 'sharp' : 'rounded'}
        ref={modalRef}
      >
        <Card.Header>
          <Modal.Title
            onClose={onClose}
            title={t('pause_subscription.put_on_hold_title')}
          />
        </Card.Header>
        <Card.Body>
          {error ? (
            <Alert
              color="danger"
              title={t(
                'pause_subscription.24_hours_to_next_payment_error_title'
              )}
            >
              <Alert.TextMessage>
                {t(
                  error.errorCode === 'NotEligible'
                    ? 'pause_subscription.24_hours_to_next_payment_error_description'
                    : 'pause_subscription.we_were_unable_to_pause_subscription'
                )}
              </Alert.TextMessage>
            </Alert>
          ) : (
            <div>
              <Box marginBottom={6}>
                <Box marginBottom={5}>
                  <BodyText>
                    {t('pause_subscription.put_on_hold_description')}
                  </BodyText>
                </Box>
                <OptionsWrapper>
                  <RadioButton
                    label={t('pause_subscription.manual_resume_option')}
                    value={ResumeOptions.MANUAL_RESUME}
                    onValueChange={(value: string) =>
                      setSelectedResumeOption(value)
                    }
                    isSelected={
                      selectedResumeOption === ResumeOptions.MANUAL_RESUME
                    }
                  />
                  <AutomaticResumeOption>
                    <RadioButton
                      label={t('pause_subscription.automatic_resume_option')}
                      value={ResumeOptions.AUTOMATIC_RESUME}
                      onValueChange={(value: string) =>
                        setSelectedResumeOption(value)
                      }
                      isSelected={
                        selectedResumeOption === ResumeOptions.AUTOMATIC_RESUME
                      }
                    />
                    <DateTimePicker
                      onChange={(dateTime: Dayjs) =>
                        setSelectedDateTime(dateTime)
                      }
                      modalRef={modalRef}
                      portalContainer={portalContainer}
                      initialDate={selectedDateTime.toISOString()}
                      minDate={dayjs().toDate()}
                      disabled={
                        selectedResumeOption !== ResumeOptions.AUTOMATIC_RESUME
                      }
                    />
                  </AutomaticResumeOption>
                </OptionsWrapper>
              </Box>
              <Alert
                color="primary"
                title={t('pause_subscription.the_plan_will_be_inactive', {
                  planName: subscription.product.name,
                })}
              >
                <Alert.TextMessage>{alertMessage()}</Alert.TextMessage>
              </Alert>
            </div>
          )}
        </Card.Body>
        <Card.Footer>
          <Button.Group alignment="right">
            {pauseSubscriptionError ? (
              <Button
                color="secondary"
                label={t('close', { ns: 'common' })}
                onClick={onClose}
              />
            ) : (
              <>
                <Button
                  color="secondary"
                  label={t('pause_subscription.keep_active')}
                  onClick={onClose}
                  disabled={pauseSubscriptionLoading}
                />
                <Button
                  label={t('pause_subscription.put_on_hold')}
                  isLoading={pauseSubscriptionLoading}
                  onClick={pauseSubscription}
                />
              </>
            )}
          </Button.Group>
        </Card.Footer>
      </Card>
      <Portal>
        <div ref={portalContainer}></div>
      </Portal>
    </>
  );
};

export default Content;
